import { Measurement } from "./Measurement";
import type { EventComponentType, GA4EventParameters } from "./google-analytics-4.type";

export const ANALYSIS_COMPONENT_TYPE: EventComponentType = "account-analysis";
export const ANALYSIS_PRODUCT_TYPES = "RETAIL_ACCOUNT-AGGREG8";

const IMPRESSION_RATIO = 0.5;
const CARD_THRESHOLD = IMPRESSION_RATIO;

// A screen taller than the viewport never reaches a ratio of 0.5, so a single threshold would never fire at all.
const SCREEN_THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

function isCardVisible(entry: IntersectionObserverEntry): boolean {
	return entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO;
}

/** Coverage of the viewport, not the element's own ratio, which a screen taller than the viewport can never reach. */
function isScreenVisible(entry: IntersectionObserverEntry): boolean {
	const viewportArea = window.innerWidth * window.innerHeight;

	if (viewportArea <= 0) {
		return false;
	}

	return entry.intersectionRect.width * entry.intersectionRect.height >= IMPRESSION_RATIO * viewportArea;
}

interface ImpressionGuard {
	sent: boolean;
}

/** Fires once, reading the parameters at fire time. Returns undefined where the environment has no IntersectionObserver. */
function observeImpressionOnce(
	element: Element,
	getParams: () => GA4EventParameters,
	isVisible: (entry: IntersectionObserverEntry) => boolean,
	threshold: number | number[],
	guard: ImpressionGuard,
): IntersectionObserver | undefined {
	if (typeof IntersectionObserver === "undefined") {
		return undefined;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			if (guard.sent || !entries.some(isVisible)) {
				return;
			}

			guard.sent = true;
			observer.disconnect();
			Measurement.sendEvent("BM_componentImpression", getParams());
		},
		{ threshold },
	);

	observer.observe(element);

	return observer;
}

export function observeCardImpressionOnce(element: Element, getParams: () => GA4EventParameters): IntersectionObserver | undefined {
	return observeImpressionOnce(element, getParams, isCardVisible, CARD_THRESHOLD, { sent: false });
}

function observeScreenImpressionOnce(element: Element, getParams: () => GA4EventParameters, guard: ImpressionGuard): IntersectionObserver | undefined {
	return observeImpressionOnce(element, getParams, isScreenVisible, SCREEN_THRESHOLDS, guard);
}

export class PageMeasurement {
	private readonly instanceId: string = Measurement.generateId("id");
	private readonly componentName: string;
	private readonly componentType: EventComponentType;
	private readonly productTypes: string;
	private observer?: IntersectionObserver;
	// At most one impression per screen instance, however many times observe() is called.
	private readonly impressionGuard: ImpressionGuard = { sent: false };

	public constructor(componentName: string, componentType: EventComponentType = ANALYSIS_COMPONENT_TYPE, productTypes: string = ANALYSIS_PRODUCT_TYPES) {
		this.componentName = componentName;
		this.componentType = componentType;
		this.productTypes = productTypes;
	}

	/** Extras are spread last, so a caller can override any of them — a list item overrides componentName. */
	public params(extra?: GA4EventParameters): GA4EventParameters {
		return {
			instanceId: this.instanceId,
			componentName: this.componentName,
			componentType: this.componentType,
			productTypes: this.productTypes,
			...extra,
		};
	}

	public componentCreate(): void {
		Measurement.sendEvent("BM_componentCreate", this.params());
	}

	/** Point this at the screen's outer wrapper: a small element could never cover half the viewport. */
	public observe(element: Element | null): void {
		if (!element) {
			return;
		}

		this.observer?.disconnect();
		this.observer = observeScreenImpressionOnce(element, () => this.params(), this.impressionGuard);
	}

	public dispose(): void {
		this.observer?.disconnect();
		this.observer = undefined;
	}
}
